import { formatInTimeZone } from 'date-fns-tz';
import { type ReactNode } from 'react';

import {
    OUTREACH_ANALYTICS_TIMEZONE,
    type OutreachUrgencyFilter,
    type OutreachWindowSelection,
} from '@apps/web/components/admin/outreach_observability/constants';
import { KpiRunsTable } from '@apps/web/components/admin/outreach_observability/kpi_runs_table';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@apps/web/components/ui/dialog';
import {
    type KpiRunMetric,
    useAdminOutreachKpiRuns,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';

// Re-exported so tile/tab components import the metric key from the drill-down
// domain rather than reaching into the data-fetching layer.
export type { KpiRunMetric };

// Server-side filters pass straight through to /kpi-runs as query params;
// `hourOfDayEt` is applied client-side (see below). `window` scopes the runs to
// the current live window so a window-scoped tile's dialog counts the same
// window as the number that opened it (mutually exclusive with `day`).
export type RunsDrillFilters = {
    readonly day?: string;
    readonly window?: OutreachWindowSelection;
    // Trailing N-ET-day window (matches the model_ab section window).
    readonly days?: number;
    // Cohort model to keep (stamped model coalesced to the default), for the
    // Model A/B tab's per-cohort run drill-down.
    readonly model?: string;
    readonly suggestedCaregiverCount?: number;
    readonly coveredShiftCountMin?: number;
    readonly coveredShiftCountMax?: number;
    readonly schedulerFollowUpCountMin?: number;
    readonly schedulerFollowUpCountMax?: number | null;
    readonly hourOfDayEt?: number;
};

// ET hour-of-day for a run's created_at, used only for the client-side
// hourOfDayEt filter.
function etHour(iso: string): number {
    return Number(
        formatInTimeZone(new Date(iso), OUTREACH_ANALYTICS_TIMEZONE, 'H'),
    );
}

// Shared runs drill-down modal: fetches the KPI runs for a metric (only while
// open) and renders them as a table. An optional `sparkline` slot renders a
// chart above the table (used by the sparklined KPI tiles); `footer` renders an
// optional action row (the hero tiles pass a "Go to <tab>" navigation link).
export function RunsDrillDialog({
    open,
    onOpenChange,
    title,
    description,
    metric,
    organizationIds,
    urgency,
    filters,
    sparkline,
    footer,
}: {
    readonly open: boolean;
    readonly onOpenChange: (open: boolean) => void;
    readonly title: string;
    readonly description?: string;
    readonly metric: KpiRunMetric;
    readonly organizationIds: Array<string>;
    readonly urgency: OutreachUrgencyFilter;
    readonly filters?: RunsDrillFilters;
    readonly sparkline?: ReactNode;
    readonly footer?: ReactNode;
}) {
    const runsQuery = useAdminOutreachKpiRuns(
        {
            metric,
            organizationIds,
            day: filters?.day,
            window: filters?.window,
            days: filters?.days,
            model: filters?.model,
            suggestedCaregiverCount: filters?.suggestedCaregiverCount,
            coveredShiftCountMin: filters?.coveredShiftCountMin,
            coveredShiftCountMax: filters?.coveredShiftCountMax,
            schedulerFollowUpCountMin: filters?.schedulerFollowUpCountMin,
            schedulerFollowUpCountMax: filters?.schedulerFollowUpCountMax,
            urgency,
        },
        open,
    );
    const data = runsQuery.data;
    const allRuns = data?.runs ?? [];
    // /kpi-runs has no hour param, so an hour-of-day drill filters the returned
    // rows client-side. Each row carries created_at, so we match on its ET hour.
    // The returned page is a newest-first cap, so total/truncation for an
    // hour-filtered view can only reflect the rows we actually have.
    const hourFiltered =
        filters?.hourOfDayEt === undefined
            ? allRuns
            : allRuns.filter(
                  (run) => etHour(run.created_at) === filters.hourOfDayEt,
              );
    const hourFilterActive = filters?.hourOfDayEt !== undefined;
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-3xl">
                <DialogHeader>
                    <DialogTitle>{title}</DialogTitle>
                    {description ? (
                        <DialogDescription>{description}</DialogDescription>
                    ) : null}
                </DialogHeader>
                {sparkline ? (
                    <div className="h-64 w-full">{sparkline}</div>
                ) : null}
                <KpiRunsTable
                    runs={hourFiltered}
                    totalMatching={
                        hourFilterActive
                            ? hourFiltered.length
                            : (data?.total_matching ?? 0)
                    }
                    // The server flag describes the full fetched page, not the
                    // client-side hour subset — passing it through would make
                    // the "showing N of M" copy contradict the counts above.
                    truncated={
                        hourFilterActive ? false : (data?.truncated ?? false)
                    }
                    isLoading={runsQuery.isLoading}
                    isError={runsQuery.error !== undefined}
                />
                {hourFilterActive && data?.truncated ? (
                    <p className="text-muted-foreground text-xs">
                        The underlying window was capped at {data.runs.length}{' '}
                        runs, so this hour may be missing older runs.
                    </p>
                ) : null}
                {footer ? <DialogFooter>{footer}</DialogFooter> : null}
            </DialogContent>
        </Dialog>
    );
}
